import React, { useState, useEffect } from 'react';
import './Makeoffer.css'; // Reuse modal overlay styles
import { CONTACT_API_URL, hasContactApi } from './api';
import emailjs from '@emailjs/browser';

const RequestAppraisalOverlay = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
  });
  const [appraisalType, setAppraisalType] = useState('sale');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset form when modal opens
      setFormData({
        title: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
      });
      setAppraisalType('sale');
      setPreferredDate('');
      setPreferredTime('');
      setShowSuccess(false);
      setIsSubmitting(false);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { title, firstName, lastName, email, phone, address } = formData;
    const name = `${title} ${firstName} ${lastName}`;

    setIsSubmitting(true);

    const emailSubject = `Appraisal Request from ${name}`;
    const detailedMessage = `
      Appraisal Type: ${appraisalType}
      Name: ${name}
      Email: ${email}
      Phone: ${phone}
      Address: ${address}
      Preferred Date: ${preferredDate}
      Preferred Time: ${preferredTime}
    `;

    if (hasContactApi()) {
      try {
        const res = await fetch(CONTACT_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            email,
            phone,
            subject: emailSubject,
            message: detailedMessage
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `Request failed: ${res.status}`);
        }
        handleSuccess();
      } catch (err) {
        console.error('Appraisal submit failed:', err);
        handleFailure();
      }
      return;
    }

    // Fallback to EmailJS for free hosting
    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';
      
      const templateParams = {
        from_name: name,
        from_email: email,
        phone: phone,
        subject: emailSubject,
        message: detailedMessage,
        to_email: 'abhi@auzlandre.com.au'
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      handleSuccess();
    } catch (err) {
      console.error('EmailJS send failed:', err);
      handleFailure();
      alert('Failed to send message. Please try again or contact us directly.');
    }
  };

  const handleSuccess = () => {
    setShowSuccess(true);
    setIsSubmitting(false);
    setTimeout(() => {
        onClose();
    }, 2000);
  };

  const handleFailure = () => {
    setIsSubmitting(false);
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="offer-modal request-appraisal-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          &times;
        </button>
        
        <div className="offer-header">
          <h3>Are you curious to find out what your property is worth?</h3>
          <p>Simply fill out the form below and we'll be in touch.</p>
        </div>

        {showSuccess ? (
            <div className="offer-success">
                <h4>Thank You!</h4>
                <p>Your appraisal request has been sent successfully.</p>
            </div>
        ) : (
            <form className="offer-form" onSubmit={handleSubmit}>
                <div className="form-group" style={{ display: 'flex', alignItems: 'left' }}>
                  <label style={{ marginRight: '1rem' }}>Appraisal type:</label>
                  <div className="radio-button-group">
                      <label className={`radio-button-label ${appraisalType === 'sale' ? 'selected' : ''}`}>
                          <input type="radio" name="appraisalType" value="sale" checked={appraisalType === 'sale'} onChange={(e) => setAppraisalType(e.target.value)} />
                          Sale
                      </label>
                      <label className={`radio-button-label ${appraisalType === 'rental' ? 'selected' : ''}`}>
                          <input type="radio" name="appraisalType" value="rental" checked={appraisalType === 'rental'} onChange={(e) => setAppraisalType(e.target.value)} />
                          Rental
                      </label>
                  </div>
              </div>
              <div className="form-group">
                  <input type="text" id="address" name="address" placeholder='Address* (E.g. 1 Johns Street, Sydney, NSW 2000)' value={formData.address} onChange={handleChange} required />
              </div>
              <div className="form-group">
                  <label>Please enter your contact details:</label>
                  <select name="title" value={formData.title} onChange={handleChange}>
                      <option value="">Title</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Ms">Ms</option>
                  </select>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                  <div className="form-group" style={{ width: '50%' }}>
                      <input type="text" id="firstName" name="firstName" placeholder="First name*" value={formData.firstName} onChange={handleChange} required />
                  </div>
                  <div className="form-group" style={{ width: '50%' }}>
                      <input type="text" id="lastName" name="lastName" placeholder="Last name*" value={formData.lastName} onChange={handleChange} required />
                  </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                  <div className="form-group" style={{ width: '50%' }}>
                      <input type="email" id="email" name="email" placeholder="Email*" value={formData.email} onChange={handleChange} required />
                  </div>
                  <div className="form-group" style={{ width: '50%' }}>
                      <input type="tel" id="phone" name="phone" placeholder="Phone" value={formData.phone} onChange={handleChange} />
                  </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group" style={{width: '50%'}}>
                    <label htmlFor="preferredDate">Preferred Date</label>
                    <input type="date" id="preferredDate" name="preferredDate" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} />
                </div>
                <div className="form-group" style={{width: '50%'}}>
                    <label htmlFor="preferredTime">Preferred Time</label>
                    <input type="time" id="preferredTime" name="preferredTime" value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} />
                </div>
              </div>
              <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
              </div>
            </form>
        )}
      </div>
    </div>
  );
};

export default RequestAppraisalOverlay;
